import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyOnboarding, BizyOnboardingFacts } from '../schemas/bizy-onboarding';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Onboarding = 'bizy-onboarding',
	OnboardingFacts = 'bizy-onboarding-facts',
}

export const bizyOnboardingQueryKey = () => [QueryKey.Onboarding];
export const bizyOnboardingFactsQueryKey = () => [QueryKey.OnboardingFacts];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyOnboardingPath = (prefix = '') => `${prefix}/bizy/onboarding`;
export const bizyOnboardingDreamingPath = (prefix = '') => `${prefix}/bizy/onboarding/perform_initial_dreaming`;
export const bizyOnboardingGatherFactsPath = (prefix = '') => `${prefix}/bizy/onboarding/gather_facts`;
export const bizyOnboardingFactsPath = (prefix = '') => `${prefix}/bizy/onboarding/facts`;

// Poll interval while gathering facts in the background.
const ONBOARDING_FACTS_REFETCH_INTERVAL_MS = 5000;

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the current user's Bizy onboarding state.
 *
 * GET /api/v2/bizy/onboarding
 *
 * `onboardingStage === 0` means the user has not started onboarding. Values
 * `>= BIZY_ONBOARDING_TOTAL_CARDS` mean onboarding is complete.
 * Gate with `enabled` (e.g. Bizy visible for the user + `bizy-onboarding`
 * feature flag) so we don't hit the endpoint for users who can't use Bizy.
 */
export const useBizyOnboarding = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyOnboardingQueryKey(),
		queryFn: async () => {
			const res = await http.get(bizyOnboardingPath(), { headers });
			return BizyOnboarding.parse(res.data);
		},
		enabled,
	});
};

/**
 * Fetches the facts Bizy has gathered about the current user for onboarding.
 * Polls while `enabled` so the caller can surface facts as they land from the
 * async gather-facts job, without a manual refetch.
 *
 * GET /api/v2/bizy/onboarding/facts
 */
export const useBizyOnboardingFacts = (enabled: boolean) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyOnboardingFactsQueryKey(),
		queryFn: async () => {
			const res = await http.get(bizyOnboardingFactsPath(), { headers });
			return BizyOnboardingFacts.parse(res.data);
		},
		enabled,
		refetchInterval: enabled ? ONBOARDING_FACTS_REFETCH_INTERVAL_MS : false,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Persists the current onboarding stage so a user can resume where they left
 * off if they close the modal mid-flow.
 *
 * PATCH /api/v2/bizy/onboarding  { onboarding_stage: number }
 *
 * `onboardingStage` is the 1-based card number the user is currently on. The
 * complete value (`100`) is sent via `useBizyCompleteOnboardingMutation`.
 */
export const useBizyUpdateOnboardingMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (onboardingStage: number): Promise<BizyOnboarding> => {
			const res = await http.patch<BizyOnboarding>(bizyOnboardingPath(), { onboarding_stage: onboardingStage }, { headers });
			return BizyOnboarding.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(bizyOnboardingQueryKey(), data);
		},
	});
};

/**
 * Kicks off Bizy's initial "dreaming" pass for the current user, seeding
 * onboarding with early context before facts are gathered.
 *
 * POST /api/v2/bizy/onboarding/perform_initial_dreaming
 */
export const useBizyPerformInitialDreamingMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async () => {
			const res = await http.post(bizyOnboardingDreamingPath(), {}, { headers });
			return res.data;
		},
	});
};

/**
 * Kicks off the async job that gathers onboarding facts about the current
 * user. Callers typically poll `useBizyOnboardingFacts` afterward to surface
 * results as they land.
 *
 * POST /api/v2/bizy/onboarding/gather_facts
 */
export const useBizyGatherFactsMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async () => {
			const res = await http.post(bizyOnboardingGatherFactsPath(), {}, { headers });
			return res.data;
		},
	});
};

// ─── Completion ───────────────────────────────────────────────────────────────

/** Canonical "onboarding complete" stage, per the backend's 0–100 range. */
export const BIZY_ONBOARDING_COMPLETE_STAGE = 100;

/**
 * Marks onboarding complete. Distinct from `useBizyUpdateOnboardingMutation`
 * so the "finished" signal is explicit and not coupled to the card count.
 *
 * PATCH /api/v2/bizy/onboarding  { onboarding_stage: 100 }
 */
export const useBizyCompleteOnboardingMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (): Promise<BizyOnboarding> => {
			const res = await http.patch<BizyOnboarding>(bizyOnboardingPath(), { onboarding_stage: BIZY_ONBOARDING_COMPLETE_STAGE }, { headers });
			return BizyOnboarding.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(bizyOnboardingQueryKey(), data);
		},
	});
};
